import axios from "axios";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL + "/api",
  headers: { "Content-Type": "application/json" },
  timeout: 15000,
});

// Attach agent token + step-up token (if cached and unexpired) to every request
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("midtc_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const stepUp        = localStorage.getItem("midtc_stepup_token");
  const stepUpExpires = parseInt(localStorage.getItem("midtc_stepup_expires") || "0", 10);
  if (stepUp && stepUpExpires > Date.now()) {
    config.headers["X-Stepup-Token"] = stepUp;
  }
  return config;
});

// Helper: prompt for password, get a step-up token, cache it 5 min, retry the
// original request. Used by callers that need to perform sensitive ops.
export async function requestStepUp() {
  const password = window.prompt(
    "🔒 Step-up authentication required\n\n" +
    "This is a sensitive operation (PowerShell, reboot, shutdown, or bulk run).\n" +
    "Please re-enter your password to continue."
  );
  if (!password) return null;
  try {
    const r = await API.post("/auth/step-up", { password });
    const expiresAt = Date.now() + (r.data.expires_in - 5) * 1000; // 5s buffer
    localStorage.setItem("midtc_stepup_token", r.data.token);
    localStorage.setItem("midtc_stepup_expires", String(expiresAt));
    return r.data.token;
  } catch (e) {
    alert("Step-up failed: " + (e.response?.data?.error || e.message));
    return null;
  }
}

// Generic wrapper: if a request returns 401 with step_up_required:true, prompt
// for password, get a token, retry once.
export async function withStepUp(fn) {
  try { return await fn(); }
  catch (e) {
    if (e.response?.status === 401 && e.response?.data?.step_up_required) {
      const tok = await requestStepUp();
      if (!tok) throw new Error("Step-up cancelled");
      return await fn();   // retry — step-up token is now in localStorage and the interceptor will attach it
    }
    throw e;
  }
}

// Auto logout on 401
API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

// Portal API instance (uses contact token)
const PORTAL_API = axios.create({
  baseURL: process.env.REACT_APP_API_URL + "/api",
  headers: { "Content-Type": "application/json" },
});

PORTAL_API.interceptors.request.use((config) => {
  const token = localStorage.getItem("midtc_portal_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

PORTAL_API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("midtc_portal_token");
      localStorage.removeItem("midtc_portal_contact");
    }
    return Promise.reject(err);
  }
);

// ── Auth (Agents) ────────────────────────────────────────────
export const login        = (email, password) => API.post("/auth/login", { email, password });
export const logout       = (refreshToken)    => API.post("/auth/logout", { refreshToken });
export const refresh      = (refreshToken)    => API.post("/auth/refresh", { refreshToken });

// ── Auth — Forgot / Reset Password ───────────────────────────
export const forgotPassword = (email)           => API.post("/auth/forgot-password", { email });
export const resetPassword  = (token, password) => API.post("/auth/reset-password", { token, password });

// ── Auth (Portal / Contacts) ─────────────────────────────────
export const portalLogin       = (email, password) => PORTAL_API.post("/portal/auth/login", { email, password });
export const setPortalPassword = (data)            => API.post("/portal/auth/set-password", data);

// M365 Portal SSO — redirect URL (not an axios call, used as href)
export const getM365PortalLoginUrl = () =>
  `${process.env.REACT_APP_API_URL}/api/auth/m365/portal/login`;

// ── Tickets ──────────────────────────────────────────────────
export const getTickets      = (params)        => API.get("/tickets", { params });
export const getTicket       = (id)            => API.get(`/tickets/${id}`);
export const createTicket    = (data)          => API.post("/tickets", data);
export const updateTicket    = (id, data)      => API.patch(`/tickets/${id}`, data);
export const addResponse     = (id, data)      => API.post(`/tickets/${id}/responses`, data,
  data instanceof FormData ? { headers: { "Content-Type": undefined } } : undefined);

// ── Ticket Dashboard ─────────────────────────────────────────
export const getDashboardTickets = (params) => API.get("/tickets/dashboard", { params });

// ── AI Suggested Response ─────────────────────────────────────
export const suggestResponse = (id, overrideContext = "") =>
  API.post(`/tickets/${id}/suggest-v2`, { override_context: overrideContext });

// ── Portal Tickets ────────────────────────────────────────────
export const getPortalTickets   = ()         => PORTAL_API.get("/portal/tickets");
export const getPortalTicket    = (id)       => PORTAL_API.get(`/portal/tickets/${id}`);
export const createPortalTicket = (data)     => PORTAL_API.post("/portal/tickets", data);
export const addPortalResponse  = (id, data) => PORTAL_API.post(`/portal/tickets/${id}/responses`, data);

// ── Ticket Tags ───────────────────────────────────────────────
export const getTags         = ()           => API.get("/tags");
export const createTag       = (data)       => API.post("/tags", data);
export const addTicketTag    = (id, tag_id) => API.post(`/tickets/${id}/tags`, { tag_id });
export const removeTicketTag = (id, tag_id) => API.delete(`/tickets/${id}/tags/${tag_id}`);

// ── Clients ──────────────────────────────────────────────────
export const getClients        = ()         => API.get("/clients");
export const createClient      = (data)     => API.post("/clients", data);
export const getClient         = (id)       => API.get(`/clients/${id}`);
export const getClientDevices  = (id)       => API.get(`/clients/${id}/devices`);
export const getClientTickets  = (id)       => API.get(`/clients/${id}/tickets`);
export const analyzeClient     = (id)       => API.post(`/clients/${id}/analyze`);
export const sendClientWelcomeEmails = (id, contactIds) =>
  API.post(`/clients/${id}/send-welcome-emails`, contactIds ? { contact_ids: contactIds } : {});
export const updateAzureTenant = (id, azure_tenant_id) =>
  API.patch(`/clients/${id}/azure-tenant`, { azure_tenant_id });

// ── Client & Website Registry (internal, staff-only) ─────────
const CDB = "/client-database";
export const cdbMeta            = ()            => API.get(`${CDB}/meta`);
export const cdbSearch          = (params)      => API.get(`${CDB}/search`, { params });
export const cdbGetClients      = ()            => API.get(`${CDB}/clients`);
export const cdbGetClient       = (id)          => API.get(`${CDB}/clients/${id}`);
export const cdbCreateClient    = (data)        => API.post(`${CDB}/clients`, data);
export const cdbUpdateClient    = (id, data)    => API.patch(`${CDB}/clients/${id}`, data);
export const cdbDeleteClient    = (id, cascade) => API.delete(`${CDB}/clients/${id}${cascade ? "?cascade=true" : ""}`);
export const cdbGetWebsites     = (params)      => API.get(`${CDB}/websites`, { params });
export const cdbGetWebsite      = (id)          => API.get(`${CDB}/websites/${id}`);
export const cdbCreateWebsite   = (data)        => API.post(`${CDB}/websites`, data);
export const cdbUpdateWebsite   = (id, data)    => API.patch(`${CDB}/websites/${id}`, data);
export const cdbDeleteWebsite   = (id)          => API.delete(`${CDB}/websites/${id}`);
export const cdbCloudwaysMeta   = ()            => API.get(`${CDB}/cloudways/meta`);
export const cdbGetCloudwaysApps   = (params)   => API.get(`${CDB}/cloudways/apps`, { params });
export const cdbUpdateCloudwaysApp = (id, data) => API.patch(`${CDB}/cloudways/apps/${id}`, data);
export const cdbExport             = (type, ids) => API.post(`${CDB}/export`, { type, ids }, { responseType: "blob" });
export const cdbGetRules        = (params)      => API.get(`${CDB}/rules`, { params });
export const cdbCreateRule      = (data)        => API.post(`${CDB}/rules`, data);
export const cdbUpdateRule      = (id, data)    => API.patch(`${CDB}/rules/${id}`, data);
export const cdbDeleteRule      = (id)          => API.delete(`${CDB}/rules/${id}`);

// ── Contacts ─────────────────────────────────────────────────
export const getContacts       = (clientId) => API.get(`/clients/${clientId}/contacts`);
export const createContact     = (data)     => API.post("/contacts", data);
export const getContact        = (id)       => API.get(`/contacts/${id}`);
export const getContactDevices = (id)       => API.get(`/contacts/${id}/devices`);
export const getContactTickets = (id)       => API.get(`/contacts/${id}/tickets`);
export const analyzeContact    = (id)       => API.post(`/contacts/${id}/analyze`);
export const resetO365Password = (id, mode, temp_password) =>
  API.post(`/contacts/${id}/o365-reset-password`, { mode, temp_password });

// ── Devices ──────────────────────────────────────────────────
export const getDevices         = (params)   => API.get("/devices", { params });
export const updateDevice       = (id, data) => API.patch(`/devices/${id}`, data);
export const getDetectedUsers   = ()         => API.get("/devices/detected-users");
export const detectDeviceUsers  = ()         => API.post("/devices/detect-users");
export const remoteDevice       = (id)       => API.post(`/devices/${id}/remote`);
export const getDeviceHistory   = (id)       => API.get(`/devices/${id}/history`);
export const getDeviceVitals    = (id, hours = 24) =>
  API.get(`/devices/${id}/vitals`, { params: { hours } });
export const getDeviceAlerts    = (id, showResolved = false) =>
  API.get(`/devices/${id}/alerts`, { params: { resolved: showResolved } });
export const resolveDeviceAlert = (deviceId, alertId) =>
  API.post(`/devices/${deviceId}/alerts/${alertId}/resolve`);
export const getDeviceHealthSummary = () => API.get("/devices/health-summary");
export const getDeviceEdrStatus     = (id) => API.get(`/devices/${id}/edr-status`);
export const getEdrInventory        = ()   => API.get(`/admin/edr/inventory`);
export const issueEdrDeploymentKey  = (clientId) => API.post(`/admin/edr/deployment-keys`, { client_id: clientId });
export const listEdrDeploymentKeys  = (clientId) =>
  API.get(`/admin/edr/deployment-keys`, { params: clientId ? { client_id: clientId } : {} });
export const revokeEdrDeploymentKey = (keyId) => API.delete(`/admin/edr/deployment-keys/${keyId}`);

// Triggers a browser download of a customized installer with embedded key
export const downloadEdrInstallerForClient = (clientId, keyId) => {
  const tok = localStorage.getItem("midtc_token");
  const url = new URL(`${process.env.REACT_APP_API_URL}/api/admin/edr/installer`);
  url.searchParams.set("client_id", clientId);
  if (keyId) url.searchParams.set("key_id", keyId);
  // Use fetch to attach the bearer header, then anchor-tag the blob to trigger download
  return fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
    .then(async r => {
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const blob = await r.blob();
      const dispo = r.headers.get("content-disposition") || "";
      const fname = (/filename="([^"]+)"/.exec(dispo)?.[1]) || "MIDTC-EDR-Setup.exe";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    });
};

// Triggers a browser download of the bundle installer EXE (both desktop + EDR)
export const downloadEdrBundleInstaller = (clientId) => {
  const tok = localStorage.getItem("midtc_token");
  const url = new URL(`${process.env.REACT_APP_API_URL}/api/admin/edr/bundle-installer`);
  url.searchParams.set("client_id", clientId);
  return fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
    .then(async r => {
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const blob = await r.blob();
      const dispo = r.headers.get("content-disposition") || "";
      const fname = (/filename="([^"]+)"/.exec(dispo)?.[1]) || "Install-MIDTC.exe";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    });
};

// Upload a custom ICO for the EDR tray icon.
// variant: "default" | "alert" | "ok"
export const uploadEdrIcon = (file, variant = "default") => {
  const tok = localStorage.getItem("midtc_token");
  const url = new URL(`${process.env.REACT_APP_API_URL}/api/admin/edr/icon`);
  url.searchParams.set("variant", variant);
  const fd = new FormData();
  fd.append("icon", file);
  return fetch(url, { method: "POST", headers: { Authorization: `Bearer ${tok}` }, body: fd })
    .then(async r => {
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
      return body;
    });
};

// Triggers a browser download of the combined bundle uninstall script (EDR + Desktop)
export const downloadBundleUninstallScript = () => {
  const tok = localStorage.getItem("midtc_token");
  const url = `${process.env.REACT_APP_API_URL}/api/admin/downloads/uninstall-bundle`;
  return fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
    .then(async r => {
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "Uninstall-MIDTC-Bundle.ps1";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    });
};

// Triggers a browser download of the PowerShell uninstall script
export const downloadEdrUninstallScript = () => {
  const tok = localStorage.getItem("midtc_token");
  const url = `${process.env.REACT_APP_API_URL}/api/admin/edr/uninstall-script`;
  return fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
    .then(async r => {
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "Uninstall-MIDTC-EDR.ps1";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    });
};

export const getEdrEvents     = (params) => API.get(`/admin/edr/events`, { params });
export const getEdrEvent      = (id)     => API.get(`/admin/edr/events/${id}`);
export const getEdrQuarantine = (params) => API.get(`/admin/edr/quarantine`, { params });
export const quarantineAction = (id, action, reason) =>
  API.post(`/admin/edr/quarantine/${id}/${action}`, { reason });
export const getEdrAgents     = ()       => API.get(`/admin/edr/agents`);
export const revokeEdrAgent   = (agentId, reason) =>
  API.post(`/admin/edr/agents/${agentId}/revoke`, { reason });
export const getEdrAuditLog   = (params) => API.get(`/admin/edr/audit-log`, { params });
export const getEdrAllowlist   = ()   => API.get(`/admin/edr/allowlist`);
export const addEdrAllowlist   = (sha256, reason, client_id, expires_at) =>
  API.post(`/admin/edr/allowlist`, { sha256, reason, client_id, expires_at });
export const removeEdrAllowlist = (sha256, client_id) =>
  API.delete(`/admin/edr/allowlist`, { params: { sha256, client_id } });
export const getEdrPolicies     = ()        => API.get(`/admin/edr/policies`);
export const getEdrPolicy       = (clientId) => API.get(`/admin/edr/policies/${clientId}`);
export const saveEdrPolicy      = (clientId, body) => API.put(`/admin/edr/policies/${clientId}`, body);
export const getEdrClients      = ()              => API.get(`/admin/edr/inventory`);   // reuses inventory for client list
export const getDeviceOverrides = (deviceId)      => API.get(`/admin/edr/devices/${deviceId}/overrides`);
export const saveDeviceOverrides = (deviceId, body) => API.put(`/admin/edr/devices/${deviceId}/overrides`, body);
export const getEdrRules         = ()         => API.get(`/admin/edr/rules`);
export const saveEdrRule         = (body)     => API.post(`/admin/edr/rules`, body);
export const deleteEdrRule       = (id)       => API.delete(`/admin/edr/rules/${id}`);
export const getEdrRuleHits      = (id)       => API.get(`/admin/edr/rules/${id}/hits`);
export const getEdrAgentDetail   = (agentId, hours = 24) =>
  API.get(`/admin/edr/agents/${agentId}/detail`, { params: { hours } });

// Device 360 — RMM command system (replaces EDR remote ops)
export const dispatchRmmCommand   = (deviceId, type, payload = {}) => API.post(`/devices/${deviceId}/dispatch`, { type, payload });
export const pollRmmCommand       = (id) => API.get(`/devices/commands/${id}`);
export const getDeviceRmmCommands = (deviceId) => API.get(`/devices/${deviceId}/commands`);
export const getDeviceToolCache   = (deviceId) => API.get(`/devices/${deviceId}/tool-cache`);
export const getDevice            = (id) => API.get(`/devices/${id}`).catch(() => API.get(`/devices`).then(r => ({ data: r.data.find(d => d.id === id) })));

// RMM saved scripts library
export const getRmmScripts  = ()     => API.get(`/rmm/scripts`);
export const saveRmmScript  = (body) => API.post(`/rmm/scripts`, body);
export const deleteRmmScript = (id)  => API.delete(`/rmm/scripts/${id}`);

// ── Server RMM (manage Windows/Linux servers — staff-only) ──────────────
export const getServerRmmMeta    = ()            => API.get(`/server-rmm/meta`);
export const getServerRmmSummary = ()            => API.get(`/server-rmm/summary`);
export const getServers          = (params)      => API.get(`/server-rmm/servers`, { params });
export const getServer           = (id)          => API.get(`/server-rmm/servers/${id}`);
export const updateServer        = (id, data)    => API.patch(`/server-rmm/servers/${id}`, data);
export const getServerCandidates = ()            => API.get(`/server-rmm/candidates`);
export const promoteToServer     = (id, body={}) => API.post(`/server-rmm/servers/${id}/promote`, body);
export const demoteServer        = (id)          => API.post(`/server-rmm/servers/${id}/demote`);
export const getServerInstallCommand = ()        => API.get(`/server-rmm/install-command`);
// Remote access: mint a short-lived single-use token for the in-browser SSH terminal (admin only).
export const startServerSshSession   = (id)      => API.post(`/server-rmm/servers/${id}/ssh-session`);
// Windows servers: force RustDesk deployment now (admin only) — fleet cron also does this every 5 min.
export const deployServerRustdesk    = (id)      => API.post(`/server-rmm/servers/${id}/rustdesk-deploy`);

// Bulk operations — wrapped so step-up prompts when the op is dangerous
export const runBulkOp           = (body)   => withStepUp(() => API.post(`/admin/edr/bulk`, body));
export const getBulkStatus       = (id)     => API.get(`/admin/edr/bulk/${id}`);
export const cancelBulkOp        = (id)     => API.delete(`/admin/edr/bulk/${id}`);

// Vendor warranty
export const lookupWarranty      = (serial, manufacturer) => API.post(`/admin/edr/warranty/lookup`, { serial, manufacturer });

// Wake-on-LAN
export const sendWoL             = (mac)    => API.post(`/admin/edr/wol`, { mac });

// Compliance
export const getDeviceCompliance = (deviceId) => API.get(`/admin/edr/devices/${deviceId}/compliance`);
export const saveCompliance      = (deviceId, snapshot, setBaseline, label) =>
  API.post(`/admin/edr/devices/${deviceId}/compliance/snapshot`, { snapshot, set_baseline: setBaseline, baseline_label: label });
export const getFleetCompliance    = ()           => API.get(`/admin/edr/compliance/fleet`);
export const getEdrEventsSummary   = (hours = 24) => API.get(`/admin/edr/events/summary`, { params: { hours } });
export const getEdrAttackCoverage  = ()           => API.get(`/admin/edr/attack-coverage`);
export const huntEvents            = (filters)    => API.post(`/admin/edr/hunt`, filters);

// ── Portal Devices ────────────────────────────────────────────
export const getPortalDevices = () => PORTAL_API.get("/portal/devices");

// Has this contact registered a device from the desktop app yet?
export const getPortalDesktopStatus = () => PORTAL_API.get("/portal/desktop-status");

// ── Agents ───────────────────────────────────────────────────
export const getAgents       = ()         => API.get("/agents");
export const createAgent     = (data)     => API.post("/agents", data);
export const updateAgent     = (id, data) => API.patch(`/agents/${id}`, data);
export const deactivateAgent = (id)       => API.patch(`/agents/${id}`, { active: false });
export const changePassword    = (data)   => API.patch("/agents/me/password", data);
export const updateMyProfile   = (data)   => API.patch("/agents/me", data);
export const uploadSignatureImage = (file) => {
  const fd = new FormData(); fd.append("image", file);
  return API.post("/agents/me/signature-image", fd, { headers: { "Content-Type": undefined } });
};

// ── Analytics ────────────────────────────────────────────────
export const getAnalyticsOverview    = ()               => API.get("/analytics/overview");
export const getAnalyticsByClient    = ()               => API.get("/analytics/by-client");
export const getAnalyticsCategories  = ()               => API.get("/analytics/categories");
export const getAnalyticsVolume      = ()               => API.get("/analytics/volume");
export const getAnalyticsBySubmitter = (limit = 20)     => API.get("/analytics/by-submitter", { params: { limit } });
export const getRepeatTickets        = (days = 30, min_count = 2) =>
  API.get("/analytics/repeat-tickets", { params: { days, min_count } });
export const getKeywordAnalytics     = (days = 30, limit = 30) =>
  API.get("/analytics/keywords", { params: { days, limit } });
export const getSLADetailAnalytics   = (days = 30)      => API.get("/analytics/sla-detail", { params: { days } });
export const getAgentPerformance     = (days = 30)      => API.get("/analytics/agent-performance", { params: { days } });
export const getTicketTrends         = (days = 90)      => API.get("/analytics/trends", { params: { days } });
export const getTagAnalytics         = ()               => API.get("/analytics/tags");

// ── Reports v2 (hybrid preset/builder) ───────────────────────
export const getReportPresets    = ()        => API.get("/reports/presets");
export const getSavedReports     = ()        => API.get("/reports/saved");
export const saveReport          = (body)    => API.post("/reports/saved", body);
export const deleteSavedReport   = (id)      => API.delete(`/reports/saved/${id}`);
export const runReport           = (body)    => API.post("/reports/run", body);
export const runAiReport         = (question) => API.post("/reports/ai", { question });
export const exportReportPdf     = (body)    => API.post("/reports/export/pdf", body, { responseType: "blob" });

// ── SLA ──────────────────────────────────────────────────────
export const getSLAPolicies  = ()         => API.get("/sla-policies");
export const createSLAPolicy = (data)     => API.post("/sla-policies", data);
export const updateSLAPolicy = (id, data) => API.patch(`/sla-policies/${id}`, data);
export const deleteSLAPolicy = (id)       => API.delete(`/sla-policies/${id}`);

// ── General Settings ──────────────────────────────────────────
export const getGeneralSettings    = ()     => API.get("/settings/general");
export const updateGeneralSettings = (data) => API.patch("/settings/general", data);

// ── Provisioning Tokens ───────────────────────────────────────
export const getProvisionTokens   = ()     => API.get("/provision-tokens");
export const createProvisionToken = (data) => API.post("/provision-tokens", data);
export const revokeProvisionToken = (id)   => API.patch(`/provision-tokens/${id}/revoke`);
export const createBootstrapToken = (data) => API.post("/provision-tokens/bootstrap", data || {});
export const getActiveBootstrapToken = ()  => API.get("/provision-tokens/bootstrap/active");

// ── Branding ─────────────────────────────────────────────────
export const getClientBranding    = (clientId)       => API.get(`/clients/${clientId}/branding`);
export const updateClientBranding = (clientId, data) => API.put(`/clients/${clientId}/branding`, data);
export const uploadClientLogo     = (clientId, file) => {
  const form = new FormData();
  form.append("logo", file);
  return API.post(`/clients/${clientId}/branding/logo`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const deleteClientLogo       = (clientId) => API.delete(`/clients/${clientId}/branding/logo`);
export const deleteClient           = (clientId) => API.delete(`/clients/${clientId}`);
export const deleteDevice           = (deviceId) => API.delete(`/devices/${deviceId}`);
export const getAdminPortalSettings = ()         => API.get("/admin/portal-settings");

// ── Branding (Public portal) ──────────────────────────────────
export const getPortalBranding = (slug) =>
  axios.get(`${process.env.REACT_APP_API_URL}/api/portal/branding/${slug}`);

// ── MeshCentral Remote Management ────────────────────────────
// getMeshcentralNodes  — lists all MeshCentral nodes with DB match status
// syncMeshcentral      — matches nodes to devices by name, writes node_id to DB
// launchMeshcentral    — returns session URL to open in new browser tab (legacy)
// getMeshcentralStatus — checks if a device is linked to a MeshCentral node
// getRemoteSession     — generates a login-token URL for embedded PiP remote desktop
// getRemoteManagement  — generates a login-token URL for embedded PiP management view
export const getMeshcentralNodes  = ()        => API.get("/meshcentral/nodes");
export const syncMeshcentral      = ()        => API.post("/meshcentral/sync");
export const launchMeshcentral    = (devId)   => API.get(`/meshcentral/launch/${devId}`);
export const getMeshcentralStatus = (devId)   => API.get(`/meshcentral/status/${devId}`);
export const getRemoteSession     = (nodeId)  => API.get(`/remote-session/${encodeURIComponent(nodeId)}`);
export const getRemoteManagement  = (nodeId)  => API.get(`/remote-session/${encodeURIComponent(nodeId)}/management`);

// ── Ticket Comments ───────────────────────────────────────────
export const getTicketComments = (id)       => API.get(`/tickets/${id}/comments`);
export const addTicketComment  = (id, data) => API.post(`/tickets/${id}/comments`, data);

// ── Atera Integration ─────────────────────────────────────────
export const ateraPreview  = ()        => API.get("/atera/preview");
export const ateraSaveKey  = (key)     => API.post("/atera/save-key", { key });
export const ateraImport   = (clearFirst = true) => API.post("/atera/import", { clearFirst });

// ── Knowledge Base (Staff) ────────────────────────────────────
export const getKBCategories    = ()         => API.get("/kb/categories");
export const createKBCategory   = (data)     => API.post("/kb/categories", data);
export const deleteKBCategory   = (id)       => API.delete(`/kb/categories/${id}`);
export const getKBArticles      = (params)   => API.get("/kb/articles", { params });
export const getKBArticle       = (id)       => API.get(`/kb/articles/${id}`);
export const createKBArticle    = (data)     => API.post("/kb/articles", data);
export const updateKBArticle    = (id, data) => API.patch(`/kb/articles/${id}`, data);
export const deleteKBArticle    = (id)       => API.delete(`/kb/articles/${id}`);
export const uploadKBImage      = (file)     => {
  const form = new FormData();
  form.append("image", file);
  return API.post("/kb/articles/image", form, { headers: { "Content-Type": "multipart/form-data" } });
};
export const assignAgentClient  = (agentId, clientId) => API.patch(`/agents/${agentId}/client`, { client_id: clientId });

// ── Knowledge Base (Portal) ───────────────────────────────────
export const getPortalKBCategories = ()       => PORTAL_API.get("/portal/kb/categories");
export const getPortalKBArticles   = (params) => PORTAL_API.get("/portal/kb/articles", { params });
export const getPortalKBArticle    = (id)     => PORTAL_API.get(`/portal/kb/articles/${id}`);

// ── Knowledge Base (Public) ───────────────────────────────────
export const getPublicKBArticles   = (params) => axios.get(`${process.env.REACT_APP_API_URL}/api/public/kb/articles`, { params });
export const getPublicKBArticle    = (id)     => axios.get(`${process.env.REACT_APP_API_URL}/api/public/kb/articles/${id}`);
export const getPublicKBCategories = ()       => axios.get(`${process.env.REACT_APP_API_URL}/api/public/kb/categories`);

// ── RMM admin status ─────────────────────────────────────────
export const getRmmStatus = () => API.get("/admin/rmm/status");

// ── Secure Data Links ────────────────────────────────────────
export const createSecureLink    = (formData) => API.post("/admin/secure-links", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const getSecureLinks      = ()          => API.get("/admin/secure-links");
export const revokeSecureLink    = (token)     => API.delete(`/admin/secure-links/${token}`);
export const getPortalSecureLink = (token)     => PORTAL_API.get(`/portal/secure-link/${token}`);
export const getSecureLinkMeta   = (token)     => PORTAL_API.get(`/secure-link/${token}/meta`);

export default API;
export { PORTAL_API };
